import { CORE_AXIOS, CORE_PLUGIN, CORE_USER, Http, type UserService } from '@/core'
import type { ConsolePageOption, ConsolePageService, Plugin } from '@/plugins'
import { PLUGIN_ACCOUNT_INFO, PLUGIN_CONSOLE_PAGE } from '@/plugins/builtin/BuiltinPluginSymbol'
import { numToDiscount } from '@/utils'
import BigNumber from 'bignumber.js'
import { defineStore, storeToRefs } from 'pinia'
import {
  container,
  injectable,
  instanceCachingFactory,
  registry,
  singleton
} from 'tsyringe'
import { watch, type Ref, type ToRefs } from 'vue'
import type { RouteRecordRaw } from 'vue-router'
import AuthCompany from './components/AuthenCompany.vue'
import AuthUser from './components/AuthenUser.vue'
import AccountBalance from './view/AccountBalance.vue'
import AccountCoupon from './view/AccountCoupon.vue'
import AccountIndex from './view/AccountIndex.vue'
import AccountCouponProdcuts from './view/CouponProducts.vue'

BigNumber.config({ ROUNDING_MODE: 1 })

export type BaseDiscount = {
  express: {
    value: number,
    name: string
  },
  clothing: {
    value: number,
    name: string
  },
  design: {
    value: number,
    name: string
  }
}

export const accountStore = defineStore('accountStore', {
  state: () => ({
    baseDiscount: {
      express: {
        value: 100,
        name: "原价"
      },
      clothing: {
        value: 100,
        name: "原价"
      },
      design: {
        value: 100,
        name: "原价"
      }
    }
  }),
  getters: {
  },
  actions: {
    async fetchBaseDiscount() {
      const axios = container.resolve<Http>(CORE_AXIOS)
      axios.get(`/v1/api/ums/base_discount/current`).then((res: any) => {
        this.$state.baseDiscount = {
          clothing: {
            value: Number(res.clothing),
            name: numToDiscount(res.clothing)
          },
          design: {
            value: Number(res.design),
            name: numToDiscount(res.design)
          },
          express: {
            value: Number(res.express),
            name: numToDiscount(res.express)
          }
        }
      }).catch(() => {
        //
      })
    },
    calculateDiscount(type: 'express' | 'clothing' | 'design', price: string | number): string {
      const discount = this.$state.baseDiscount[type]
      const discountedPrice = new BigNumber(price).times(new BigNumber(discount.value).dividedBy(100)).toFixed(2)
      return discountedPrice
    }
  }
})

export type AccountBaseInfoOption = {
  baseDiscount: Ref<BaseDiscount>
}

export interface AccountInfoService {
  calculateDiscount(type: 'express' | 'clothing' | 'design', price: string | number): string
}

/**
 * 插件的Option定义
 */
@singleton()
@registry([
  {
    token: CORE_PLUGIN,
    useFactory: instanceCachingFactory<Plugin<ToRefs<AccountBaseInfoOption>, AccountInfoService>>(
      (c) => c.resolve(AccountInfoPLugin)
    ),
  },
])
@registry([
  {
    token: PLUGIN_ACCOUNT_INFO,
    useFactory: instanceCachingFactory<Plugin<ToRefs<AccountBaseInfoOption>, AccountInfoService>>(
      (c) => c.resolve(AccountInfoPLugin)
    ),
  },
])
@injectable()
export class AccountInfoPLugin implements Plugin<ToRefs<AccountBaseInfoOption>, AccountInfoService> {
  id: string | symbol = ''
  options: ToRefs<AccountBaseInfoOption>
  service: AccountInfoService
  constructor() {
    const instantiationStore = accountStore()
    const options = storeToRefs(instantiationStore)
    this.service = instantiationStore
    this.options = options as ToRefs<AccountBaseInfoOption>
  }

  init(): void {
    const plugin = container.resolve<Plugin<ConsolePageOption, ConsolePageService>>(PLUGIN_CONSOLE_PAGE)
    const accountRoute = [
      { name: "account-balance", path: "accounts/balance", component: AccountBalance },
      { name: "account-home", path: "accounts/profile", component: AccountIndex },
      { name: "account-companyAccount", path: "accounts/profile/companyAccount", component: AuthCompany },
      { name: "account-userAccount", path: "accounts/profile/userAccount", component: AuthUser },
      { name: "account-couponClaim", path: "accounts/profile/couponClaim", component: AccountCoupon },
      { name: "account-couponProducts", path: "accounts/profile/couponProducts/:id", component: AccountCouponProdcuts },
    ] as RouteRecordRaw[]
    accountRoute.forEach(item => {
      plugin.service.addChildRoute(item)
    })

    const account = accountStore()
    // watch and refresh operations
    const userService = container.resolve<UserService>(CORE_USER)
    if (userService.isUserValid()) {
      account.fetchBaseDiscount()
    }
    const user = userService.getUser()
    watch(user, () => {
      account.fetchBaseDiscount()
    }, { deep: true })
  }

  initialized(): void {
    //
  }
}
